import API from '~config/api';
import {
  ApiPromise,
  DataResponse,
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
  FileObj,
} from '~globals/types';

import {
  AdminBillingListBase,
  AdminBillingListFilter,
  AdmingBillingListCreateBody,
  AdmingBillingListUpdateBody,
  AdminBillingListAssignToCompanyIntegrationBody,
  AdminBillingListAssignToGiverCompanyBody,
} from './types';

export const BILLING_LIST_ENDPOINTS = {
  getAdmingBillingsList: '/api/v1/Route/BillingList',
  getAdmingBillingListById: (billingListId: string): string =>
    `/api/v1/Route/BillingList/${billingListId}`,
  createAdmingBillingList: '/api/v1/Route/BillingList',
  updateAdmingBillingList: '/api/v1/Route/BillingList',
  deleteAdmingBillingList: (billingListId: string): string =>
    `/api/v1/Route/BillingList/${billingListId}`,
  searchAdminBillingsList: '/api/v1/Route/BillingList/search',
  importAdmingBillingList: '/api/v1/Route/BillingList/Import',
  exportAdmingBillingList: '/api/v1/Route/BillingList/Export',
  assignBillingListToCompanyIntegration:
    '/api/v1/Route/BillingList/AssignBIllingListCompanyIntegration',
  assignBillingLisTotGiverCompany:
    '/api/v1/Route/BillingList/AssignBIllingListGiverCompany',
};

export const getAdmingBillingsList = (): ApiPromise<
  DataResponse<AdminBillingListBase[]>,
  ErrorResponse
> =>
  API.get<DataResponse<AdminBillingListBase[]>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.getAdmingBillingsList,
  );

export const getAdmingBillingListById = (
  billingListId: string,
): ApiPromise<DataResponse<AdminBillingListBase>, ErrorResponse> =>
  API.get<DataResponse<AdminBillingListBase>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.getAdmingBillingListById(billingListId),
  );

export const createAdmingBillingList = (
  body: AdmingBillingListCreateBody,
): ApiPromise<DataResponse<AdminBillingListBase>, ErrorResponse> =>
  API.post<DataResponse<AdminBillingListBase>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.createAdmingBillingList,
    body,
  );

export const updateAdmingBillingList = (
  body: AdmingBillingListUpdateBody,
): ApiPromise<DataResponse<AdminBillingListBase>, ErrorResponse> =>
  API.put<DataResponse<AdminBillingListBase>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.updateAdmingBillingList,
    body,
  );

export const deleteAdmingBillingList = (
  billingListId: string,
): ApiPromise<DataResponse<AdminBillingListBase>, ErrorResponse> =>
  API.delete<DataResponse<AdminBillingListBase>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.deleteAdmingBillingList(billingListId),
  );

export const searchAdminBillingsList = ({
  pagination: { page, sizePerPage },
  filters,
}: PaginationRequestParams<AdminBillingListFilter>): ApiPromise<
  DataPaginateResponse<AdminBillingListBase[]>,
  ErrorResponse
> =>
  API.post<DataPaginateResponse<AdminBillingListBase[]>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.searchAdminBillingsList,
    { ...filters, pagination: { offset: page, size: sizePerPage } },
  );

export const importAdmingBillingList = (
  file: FileObj,
): ApiPromise<DataResponse<null>, ErrorResponse> => {
  const formData = new FormData();

  formData.append('file', file.file, file.file.name);

  return API.post<DataResponse<null>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.importAdmingBillingList,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'multipart/form-data',
      },
    },
  );
};

export const exportAdmingBillingList = (): ApiPromise<Blob, ErrorResponse> =>
  API.get<Blob, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.exportAdmingBillingList,
    {},
    { responseType: 'blob', timeout: 120000 },
  );

export const assignBillingListToCompanyIntegration = (
  body: AdminBillingListAssignToCompanyIntegrationBody,
): ApiPromise<DataResponse<null>, ErrorResponse> =>
  API.post<DataResponse<null>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.assignBillingListToCompanyIntegration,
    body,
  );

export const assignBillingLisToGiverCompany = (
  body: AdminBillingListAssignToGiverCompanyBody,
): ApiPromise<DataResponse<null>, ErrorResponse> =>
  API.post<DataResponse<null>, ErrorResponse>(
    BILLING_LIST_ENDPOINTS.assignBillingLisTotGiverCompany,
    body,
  );
